/**
 * Comment Event Handlers
 * Pure event handling methods extracted from CommentEventHandler
 */

// @ts-check

import { LikeRender } from "../like/like-render";
import { LikeForm } from "../like/like-form";
import { CommonUtils, HtmlInsertPosition } from "../util/common-utils";

/**
 * @typedef {Object} MediaPathConfig
 * @property {string} urlPath
 * @property {string} defaultFilename
 */

/**
 * @typedef {Object} MediaPaths
 * @property {MediaPathConfig} avatar
 */

/**
 * Extend Window interface for custom properties
 * @typedef {Window & {
 *   MediaPaths?: MediaPaths,
 *   authUser?: any,
 *   commentRender?: any,
 *   DataTranslater?: any,
 *   Notifier?: any,
 *   dropdownToggle?: any,
 *   commentEditToggle?: any,
 *   postsData?: any,
 *   dropdownEventHandler?: any,
 *   LikeStateManager?: any,
 *   elementModalManager?: any
 * }} ExtendedWindow
 */

type CommentPostData = Omit<import("../types/php-injected").PhpPostData, "comments"> & {
    comments?: RenderableComment[];
    comment_like_states?: Record<string, { is_liked?: boolean; likes_count?: number }>;
};

import { TaskScheduler } from "../core/task-scheduler";
import { CommentApiService } from "./comment-api";
import { CommentValidationUtils } from "./comment-validation-utils";
import { spinner } from "../util/spinner/spinner";
import { notifier } from "../core/notifier/notifier";
import { translator } from "../translation/translator";
import { dropdownToggle } from "../util/dropdown/dropdown-toggle";
import { dropdownHandler } from "../util/dropdown/dropdown-handler";
import { commentToggleHandler } from "./comment-toggle-handler";
import { commentRender } from "./comment-render";
import type { CommentItemId, RenderableComment } from "./comment-render";
import { commentForm } from "./comment-form";
import { TextareaMentionSetup } from "./comment-mention-setup";
import { modalHandler } from "../modal/modal-handler";
import { phpPostData } from "../core/php-data-access";

function toCommentId(rawCommentId: string | undefined): number | null {
    const commentId = Number(rawCommentId);
    return Number.isInteger(commentId) && commentId > 0 ? commentId : null;
}

class CommentHandler {
    public commentsContainer: Element | null;

    public likeRender: LikeRender;

    public submitHandler: ((event: Event) => void | Promise<void>) | undefined;

    public deleteHandler: ((event: Event) => void | Promise<void>) | undefined;

    public replyToggleHandler: ((event: Event) => void | Promise<void>) | undefined;

    public dropdownHandler: ((event: Event) => void | Promise<void>) | undefined;

    public editHandler: ((event: Event) => void | Promise<void>) | undefined;

    public cancelEditHandler: ((event: Event) => void | Promise<void>) | undefined;

    public constructor() {
        this.commentsContainer = commentForm.getCommentsSection(document);

        // Dynamic handler properties (assigned externally)
        this.submitHandler = undefined;
        this.deleteHandler = undefined;
        this.replyToggleHandler = undefined;
        this.dropdownHandler = undefined;
        this.editHandler = undefined;
        this.cancelEditHandler = undefined;
        this.likeRender = new LikeRender(new LikeForm());
    }

    public revealPendingTimestamps(root: ParentNode): void {
        commentForm.getPendingTimestamps(root).forEach((element) => {
            element.style.opacity = "1";
            element.removeAttribute("data-pending-display");
        });
    }

    public getCommentPostData(postId: number): CommentPostData | null {
        const postData = phpPostData(postId) as CommentPostData | null;
        return postData;
    }

    /**
     * Handle form submission
     */
    public async handleSubmit(e) {
        const form = commentForm.getEventTarget(e);
        if (commentForm.isCommentSubmitForm(form)) {
            e.preventDefault();
            e.stopPropagation();

            if (commentForm.isEditInnerForm(form)) {
                await this.handleEditSubmit(form);
            } else {
                await this.handleCommentSubmit(form);
            }
        }
    }

    /**
     * Handle delete button clicks
     */
    public handleDelete(e) {
        const target = commentForm.getEventTarget(e);
        const deleteBtn = commentForm.getClosestCommentDeleteAction(target);
        if (deleteBtn instanceof HTMLElement) {
            e.preventDefault();
            e.stopPropagation();
            this.handleDeleteClick(deleteBtn);
        }
    }

    /**
     * Handle reply toggle clicks
     */
    public handleReplyToggle(e) {
        const target = commentForm.getEventTarget(e);
        const btn = commentForm.getClosestReplyToggleButton(target);
        if (btn) {
            e.preventDefault();
            const commentId = toCommentId(btn.dataset.commentId);
            if (commentId !== null) {
                this.toggleReplies(commentId);
            }
        }
    }

    /**
     * Handle dropdown closing
     */
    public handleDropdown(e) {
        const clickedInsideMenu = commentForm.getClosestCommentMenuTrigger(e);
        if (!clickedInsideMenu) {
            if (dropdownToggle) {
                dropdownToggle.hideAllDropdownMenus();
            }
        }
    }

    /**
     * Handle edit button clicks
     */
    public handleEdit(e) {
        const target = commentForm.getEventTarget(e);
        if (!target) return;
        const button = commentForm.getClosestCommentEditAction(target);
        if (button) {
            e.preventDefault();
            const commentId = toCommentId(button.dataset.commentId);
            if (commentId !== null && commentToggleHandler) {
                commentToggleHandler.showEditForm(commentId);
            }
        }
    }

    /**
     * Handle cancel edit clicks
     */
    public handleCancelEdit(e) {
        const target = commentForm.getEventTarget(e);
        if (!target) return;
        if (commentForm.getClosestEditCancel(target)) {
            e.preventDefault();
            const form = commentForm.getClosestEditInnerForm(target);
            const commentId = form instanceof HTMLFormElement ? toCommentId(form.dataset.commentId) : null;
            if (commentId !== null && commentToggleHandler) {
                commentToggleHandler.hideEditForm(commentId);
            }
        }
    }

    /**
     * Handle comment/reply form submission
     */
    public async handleCommentSubmit(form: HTMLFormElement): Promise<void> {
        if (!CommentValidationUtils.validateCommentForm(form)) {
            return;
        }

        const textarea = commentForm.getCommentContentTextarea(form);
        const content = textarea ? TextareaMentionSetup.getHtmlContent(textarea).trim() : undefined;
        const isReply = commentForm.isReplyInnerForm(form);
        const parentId = isReply ? commentForm.getParentIdInput(form)?.value : null;

        const submitBtn = commentForm.getSubmitButton(form);

        // Show spinner on button
        if (submitBtn) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
        }

        if (textarea) {
            textarea.disabled = true;
        }

        try {
            const { boardId, postId } = CommentApiService.getBoardAndPostIds();

            // Call API first
            const data = await CommentApiService.createComment(
                boardId,
                postId,
                content,
                parentId ? Number(parentId) : null,
            );

            if (!data || !data.comment_id) {
                throw new Error("Invalid API response structure");
            }

            // Clear form
            if (textarea) {
                textarea.value = "";
            }

            // Hide reply form if it was a reply
            if (isReply && parentId) {
                this.toggleReplyForm(Number(parentId));
            }

            // Add optimistic comment to DOM immediately without re-fetching
            const mediaPaths = Reflect.get(window, "MediaPaths");
            const avatar = mediaPaths && typeof mediaPaths === "object" ? Reflect.get(mediaPaths, "avatar") : null;
            const avatarPath = avatar && typeof avatar === "object" ? Reflect.get(avatar, "urlPath") : null;
            const defaultAvatar = avatar && typeof avatar === "object" ? Reflect.get(avatar, "defaultFilename") : null;
            const normalizedAvatarPath =
                typeof avatarPath === "string" && avatarPath.trim() !== "" && avatarPath.endsWith("/")
                    ? avatarPath.slice(0, -1)
                    : avatarPath;
            const defaultAvatarUrl =
                normalizedAvatarPath && defaultAvatar ? `${normalizedAvatarPath}/${defaultAvatar}` : null;

            const authUser = Reflect.get(window, "authUser");
            const currentUser =
                authUser && typeof authUser === "object"
                    ? authUser
                    : {
                          user_id: 0,
                          display_name: "User",
                          avatar: defaultAvatarUrl,
                      };
            const newComment: RenderableComment = {
                comment_id: data.comment_id as CommentItemId,
                post_id: Number(postId),
                user_id: currentUser.user_id,
                content: content,
                parent_comment_id: parentId ? Number(parentId) : null,
                replies_count: 0,
                like_count: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                user: {
                    user_id: currentUser.user_id,
                    display_name: currentUser.display_name,
                    avatar: currentUser.avatar,
                },
                replies: [],
            };

            // Render and insert new comment
            if (commentRender) {
                const commentSection = commentForm.getClosestCommentsSection(form);
                const searchRoot = commentSection || commentForm.getSearchRoot(form);
                const commentsList = commentForm.getCommentsList(searchRoot);
                if (commentsList) {
                    // If the thread was empty, remove placeholder(s) before inserting the new comment
                    const noCommentsEl = commentForm.getCommentEmpty(commentsList);
                    if (noCommentsEl) {
                        noCommentsEl.remove();
                    }
                    const noCommentsYetEl = commentForm.getCommentEmptyYet(commentsList);
                    if (noCommentsYetEl) {
                        noCommentsYetEl.remove();
                    }

                    const commentHTML = commentRender.renderCommentItem(newComment);
                    if (isReply && parentId) {
                        // Insert reply into parent's replies section
                        const parentComment = commentForm.getCommentItem(commentsList, Number(parentId));
                        if (parentComment) {
                            const repliesContainer = commentForm.getRepliesContainer(parentComment);
                            if (repliesContainer) {
                                CommonUtils.insertHtml(repliesContainer, commentHTML, HtmlInsertPosition.BEFORE_END);
                            }
                        }
                    } else {
                        // Insert top-level comment at the beginning
                        CommonUtils.insertHtml(commentsList, commentHTML, HtmlInsertPosition.AFTER_BEGIN);
                    }

                    // Show newly added timestamps
                    this.revealPendingTimestamps(commentsList);

                    // Update comment count
                    const allComments = commentForm.getCommentItems(commentsList);
                    const realComments = Array.from(allComments).filter((el) => {
                        if (!(el instanceof Element)) return false;
                        const id = el.getAttribute("data-comment-id");
                        return id && !id.startsWith("temp_");
                    });
                    const contextElement = commentSection || commentForm.getClosestCommentsSectionClass(commentsList);
                    this.updateCommentCountDisplay(realComments.length, contextElement);
                }
            }

            // Scroll to new comment after DOM is fully updated
            TaskScheduler.schedule(() => {
                TaskScheduler.schedule(() => {
                    this.scrollToCommentById(data.comment_id, 0, form);
                }, "raf");
            }, "raf");

            // Show success
            notifier.success("comment_posted", {}); // Note: Parent comment count is updated by reloadFromParent
        } catch (error) {
            console.error("Comment submission error:", error);
            notifier.error("comments_post_error", {});
            // Remove optimistic comment on error
            const tempElements = commentForm.getTempCommentItems(document);
            tempElements.forEach((el) => {
                el.remove();
            });
        } finally {
            // Hide spinner and restore button
            if (submitBtn) {
                spinner.hideButtonLoader(submitBtn);
            }
            if (textarea) {
                textarea.disabled = false;
            }
        }
    }

    /**
     * Handle edit form submission
     */
    public async handleEditSubmit(form: HTMLFormElement): Promise<void> {
        if (!CommentValidationUtils.validateCommentForm(form)) {
            return;
        }

        const textarea = commentForm.getCommentContentTextarea(form);
        const content = textarea ? TextareaMentionSetup.getHtmlContent(textarea).trim() : undefined;
        const commentId = toCommentId(form.dataset.commentId);

        if (commentId === null) {
            notifier.error("comments_edit_error", {});
            return;
        }

        const submitBtn = commentForm.getSubmitButton(form);

        // Show spinner on button
        if (submitBtn) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
        }

        try {
            const { boardId, postId } = CommentApiService.getBoardAndPostIds();
            await CommentApiService.updateComment(boardId, postId, commentId, content);

            // Update comment text display
            const commentItemEl = commentForm.getCommentItem(document, commentId);
            const commentText = commentItemEl ? commentForm.getCommentText(commentItemEl) : null;
            if (commentText) {
                CommonUtils.setCommentTextWithLineBreaks(commentText, content || "");
            }

            // Hide spinner BEFORE hiding form
            if (submitBtn) {
                spinner.hideButtonLoader(submitBtn);
            }

            // Hide edit form using toggle
            if (commentToggleHandler) {
                commentToggleHandler.hideEditForm(commentId);
            }

            // Highlight updated comment
            const highlightedComment = commentForm.getCommentItem(document, commentId);
            if (highlightedComment) {
                highlightedComment.classList.add("comment-highlight");
                setTimeout(() => {
                    highlightedComment.classList.remove("comment-highlight");
                }, 1000);
            }

            // Show success
            notifier.success("comment_updated", {});

            // Note: Parent comment count sync is not needed for edit (count doesn't change)
        } catch (error) {
            console.error(error);
            notifier.error("comments_edit_error", {});
        } finally {
            // Hide spinner
            if (submitBtn) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }

    /**
     * Handle delete button click
     */
    public async handleDeleteClick(button: HTMLElement): Promise<void> {
        const commentId = toCommentId(button.dataset.commentId);

        if (commentId === null) {
            return;
        }

        const ok = await notifier.confirm("comments_delete_confirm", {
            translateKey: "comments_delete_confirm",
            okTranslateKey: "delete",
            cancelTranslateKey: "cancel",
        });

        if (!ok) {
            return;
        }

        const commentSection = commentForm.getClosestCommentsSection(button);
        const searchRoot = commentSection || commentForm.getSearchRoot(button);

        const commentElementCandidate =
            (commentSection ? commentForm.getCommentItem(commentSection, commentId) : null) ||
            commentForm.getCommentItem(searchRoot, commentId) ||
            null;
        const commentElement = commentElementCandidate instanceof HTMLElement ? commentElementCandidate : null;

        if (commentElement) {
            commentElement.style.opacity = "0.5";
            commentElement.style.pointerEvents = "none";
        }

        try {
            const { boardId, postId } = CommentApiService.getBoardAndPostIds();
            await CommentApiService.deleteComment(boardId, postId, commentId);

            // Remove comment from DOM
            if (commentElement) {
                commentElement.remove();
            }

            // Update comment count (exclude optimistic temp_ comments)
            const commentsListContainer =
                (commentSection ? commentForm.getCommentsList(commentSection) : null) ||
                commentForm.getCommentsList(searchRoot) ||
                null;

            if (commentsListContainer) {
                const allComments = commentForm.getCommentItems(commentsListContainer);
                const realComments = Array.from(allComments).filter((el) => {
                    const id = el.getAttribute("data-comment-id");
                    return id && !id.startsWith("temp_");
                });
                const newCount = realComments.length;
                const contextElement =
                    commentSection || commentForm.getClosestCommentsSectionClass(commentsListContainer);
                this.updateCommentCountDisplay(newCount, contextElement);
            } else {
                console.error("[handleDeleteClick] Comments list container not found");
            }

            // Show success
            notifier.success("comment_deleted", {});

            // Note: Parent comment count is updated by updateCommentCountDisplay above
        } catch (error) {
            console.error(error);
            notifier.error("comments_delete_error", {});

            // Restore comment state
            if (commentElement) {
                commentElement.style.opacity = "1";
                commentElement.style.pointerEvents = "auto";
            }
        }
    }

    /**
     * Toggle reply form visibility
     */
    public toggleReplyForm(commentId: number): void {
        const commentItemEl = commentForm.getCommentItem(document, commentId);
        if (!commentItemEl) return;

        const replyForm = commentForm.getReplyForm(commentItemEl);
        if (!replyForm) return;

        const isVisible = replyForm.style.display !== "none";

        // Hide all reply forms
        commentForm.getReplyForms(document).forEach((form) => {
            if (form instanceof HTMLElement) {
                form.style.display = "none";
            }
        });

        // Toggle current form
        if (!isVisible) {
            replyForm.style.display = "block";
            const textarea = commentForm.getReplyTextarea(replyForm);
            if (textarea) {
                textarea.focus();
            }
        }
    }

    /**
     * Toggle replies visibility
     */
    public async toggleReplies(commentId: number): Promise<void> {
        const commentItemEl = commentForm.getCommentItem(document, commentId);
        if (!commentItemEl) return;

        const repliesContainer = commentForm.getRepliesContainer(commentItemEl);
        if (!repliesContainer) return;

        const isVisible = repliesContainer.style.display !== "none";

        if (!isVisible) {
            repliesContainer.style.display = "block";

            // Load replies if not already loaded
            if (commentForm.getRepliesLoading(repliesContainer)) {
                await this.loadReplies(commentId);
            }
        } else {
            repliesContainer.style.display = "none";
        }
    }

    /**
     * Add new comment to DOM dynamically
     */
    public addCommentToDOM(comment: RenderableComment, isReply: boolean, parentId: number | null): void {
        try {
            // Determine search root based on modal context
            const searchRoot = commentForm.getSearchRoot();

            if (!searchRoot) {
                console.error("Search root not found for addCommentToDOM");
                return;
            }

            if (isReply && parentId) {
                // Add reply to parent's replies container
                const parentComment = commentForm.getCommentItem(searchRoot, parentId);
                const repliesContainer = parentComment ? commentForm.getRepliesContainer(parentComment) : null;
                if (repliesContainer) {
                    const replyHTML = commentRender.renderCommentItem(comment);
                    CommonUtils.insertHtml(repliesContainer, replyHTML, HtmlInsertPosition.BEFORE_END);

                    // Show newly added timestamps
                    this.revealPendingTimestamps(repliesContainer);

                    // Show replies container if hidden
                    repliesContainer.style.display = "block";
                }
            } else {
                // Add new comment to top of comments list
                const commentsListContainer = commentForm.getCommentsList(searchRoot);

                if (commentsListContainer) {
                    const commentHTML = commentRender.renderCommentItem(comment);

                    CommonUtils.insertHtml(commentsListContainer, commentHTML, HtmlInsertPosition.AFTER_BEGIN);

                    // Show newly added timestamps
                    this.revealPendingTimestamps(commentsListContainer);

                    // Update comment count (exclude optimistic temp_ comments)
                    const allComments = commentForm.getCommentItems(commentsListContainer);
                    const realComments = Array.from(allComments).filter((el) => {
                        const commentId = el.getAttribute("data-comment-id");
                        return commentId && !commentId.startsWith("temp_");
                    });
                    const newCount = realComments.length;
                    this.updateCommentCountDisplay(
                        newCount,
                        commentForm.getClosestCommentsSection(commentsListContainer),
                    );
                } else {
                    console.error(
                        "Comments list container not found in",
                        commentForm.isElementModalOpen() ? "modal" : "page",
                    );
                }
            }

            // Re-translate newly added elements
            translator.updateContent();
        } catch (error) {
            console.error("Error in addCommentToDOM:", error);
            this.reloadComments();
        }
    }

    /**
     * Add new comment to parent postsData
     */
    public addCommentToParentData(postId: number, comment: RenderableComment): void {
        const postData = this.getCommentPostData(postId);
        if (!postData) {
            console.warn(`[addCommentToParentData] Post ${postId} not found`);
            return;
        }

        const comments: RenderableComment[] = Array.isArray(postData.comments)
            ? postData.comments.filter(
                  (comment): comment is RenderableComment =>
                      !!comment && typeof comment === "object" && !Array.isArray(comment),
              )
            : [];

        // Add to beginning for newest-first display
        comments.unshift(comment);
        postData.comments = comments;

        // Update comment count
        postData.comments_count = comments.length;
    }

    /**
     * Reload comments from parent postsData (simple structure)
     */
    public async reloadFromParent(postId: number): Promise<void> {
        // Check if commentRender is available
        if (!commentRender) {
            console.warn("[reloadFromParent] commentRender not available, falling back to reloadComments");
            await this.reloadComments();
            return;
        }

        const postData = phpPostData(postId);
        if (!postData) {
            console.warn(`[reloadFromParent] Post ${postId} not found, falling back to reloadComments`);
            await this.reloadComments();
            return;
        }

        // Determine search root based on modal context
        const searchRoot = commentForm.getSearchRoot();

        const commentsListContainer = commentForm.getCommentsList(searchRoot);
        if (!commentsListContainer) {
            console.warn("[reloadFromParent] comments list container not found");
            return;
        }

        const comments = Array.isArray(postData.comments)
            ? postData.comments.reduce<RenderableComment[]>((result, comment) => {
                  if (comment && typeof comment === "object" && !Array.isArray(comment)) {
                      result.push(comment as RenderableComment);
                  }
                  return result;
              }, [])
            : [];

        if (comments.length > 0) {
            try {
                CommonUtils.setHtml(
                    commentsListContainer,
                    comments.map((comment) => commentRender.renderCommentItem(comment)).join(""),
                );

                // Show all timestamps
                this.revealPendingTimestamps(commentsListContainer);

                // Restore comment like states from parent data
                this.restoreCommentLikeStates(postId);
            } catch (error) {
                console.error("[reloadFromParent] Error rendering comments:", error);
                await this.reloadComments();
                return;
            }
        } else {
            if (commentRender) {
                CommonUtils.setHtml(commentsListContainer, commentRender.renderNoCommentsYet());
            }
        }

        // Update count display
        this.updateCommentCountDisplay(comments.length, commentForm.getClosestCommentsSection(commentsListContainer));

        // Update language
        translator.updateContent();
    }

    /**
     * Load replies for a comment
     */
    public async loadReplies(commentId: number): Promise<void> {
        const commentItemEl = commentForm.getCommentItem(document, commentId);
        if (!commentItemEl) return;

        const repliesContainer = commentForm.getRepliesContainer(commentItemEl);
        if (!repliesContainer) return;

        try {
            const { boardId, postId } = CommentApiService.getBoardAndPostIds();
            const data = await CommentApiService.fetchReplies(boardId, postId, commentId);
            const replies = Array.isArray(data.data) ? (data.data as RenderableComment[]) : [];

            // Render replies using view (if available)
            if (commentRender) {
                CommonUtils.setHtml(repliesContainer, commentRender.renderReplies(replies));
            } else {
                CommonUtils.setHtml(repliesContainer, commentRender?.renderRepliesLoading(replies.length) || "");
            }
        } catch (error) {
            console.error(error);
            if (commentRender) {
                CommonUtils.setHtml(repliesContainer, commentRender.renderRepliesLoadError());
            }
        }
    }

    /**
     * Reload all comments
     */
    public async reloadComments(): Promise<void> {
        // Determine search root based on modal context
        const searchRoot = commentForm.getSearchRoot();

        const commentsListContainer = commentForm.getCommentsList(searchRoot);

        if (!commentsListContainer) {
            return;
        }

        try {
            const { boardId, postId } = CommentApiService.getBoardAndPostIds();

            if (!boardId || !postId) {
                return;
            }

            if (commentRender) {
                CommonUtils.setHtml(commentsListContainer, commentRender.renderLoadingComments());
            }

            const data = await CommentApiService.fetchComments(boardId, postId);

            if (Array.isArray(data.data)) {
                if (data.data.length > 0) {
                    // Use JS render function for proper rendering
                    if (commentRender) {
                        CommonUtils.setHtml(commentsListContainer, commentRender.renderCommentsHTML(data.data));

                        // Show all timestamps
                        this.revealPendingTimestamps(commentsListContainer);

                        // Store comment like states to parent data (if in modal)
                        const isInElementModal = commentForm.isElementModalOpen();
                        const postData = phpPostData(postId);
                        if (isInElementModal && postData) {
                            this.saveCommentLikeStatesToParent(postId, data.data);

                            // Restore like states from parent data (in case user liked in previous modal session)
                            this.restoreCommentLikeStates(postId);
                        }
                    } else {
                        console.error("[reloadComments] commentRender not available");
                        CommonUtils.setHtml(commentsListContainer, commentRender?.RenderrorLoadingComments() || "");
                    }
                    this.updateCommentCountDisplay(
                        data.data.length,
                        commentForm.getClosestCommentsSection(commentsListContainer),
                    );
                } else {
                    if (commentRender) {
                        CommonUtils.setHtml(commentsListContainer, commentRender.renderNoComments());
                    }
                    this.updateCommentCountDisplay(0, commentForm.getClosestCommentsSection(commentsListContainer));
                }
            } else {
                if (commentRender) {
                    CommonUtils.setHtml(commentsListContainer, commentRender.renderNoComments());
                }
                this.updateCommentCountDisplay(0, commentForm.getClosestCommentsSection(commentsListContainer));
            }

            // Reinitialize functionality
            TaskScheduler.schedule(() => {
                // Dropdown menus use event delegation, so no re-initialization needed
                // Just ensure the dropdown event handler is initialized (should already be done in app.js)
                if (dropdownHandler && !dropdownHandler.isInitialized) {
                    dropdownHandler.initialize();
                }
                // Update language
                translator.updateContent();
            }, "dom");
        } catch (error) {
            if (commentRender) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
                CommonUtils.setHtml(commentsListContainer, commentRender.RenderrorLoadingComments(errorMessage));
            }
        }
    }

    /**
     * Scroll to newest comment
     */
    public scrollToNewComment(): void {
        // Check if we're in element-modal context
        const searchRoot = commentForm.getSearchRoot();

        if (!searchRoot) {
            console.warn("Search root not found for scrollToNewComment");
            return;
        }

        const commentsSection = commentForm.getCommentsSection(searchRoot);
        const commentsListContainer = commentForm.getCommentsList(searchRoot);

        if (!commentsSection || !commentsListContainer) {
            console.warn("Comments section or list not found", {
                commentsSection: !!commentsSection,
                commentsListContainer: !!commentsListContainer,
            });
            return;
        }

        const firstComment = commentForm.getCommentItems(commentsListContainer)[0] || null;

        if (firstComment) {
            // Scroll to comments section first for better visual flow
            commentsSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });

            // Then scroll to first comment after brief delay
            setTimeout(() => {
                firstComment.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    inline: "nearest",
                });

                firstComment.style.transition = "background-color 0.3s ease";
                firstComment.style.backgroundColor = "#e8f5e8";

                setTimeout(() => {
                    firstComment.style.backgroundColor = "";
                    TaskScheduler.schedule(() => {
                        firstComment.style.transition = "";
                    }, "raf");
                }, 2000);
            }, 300);
        } else {
            commentsSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        }
    }

    /**
     * Scroll to specific comment by ID and highlight it
     */
    public scrollToCommentById(commentId: number, retryCount = 0, contextElement: HTMLElement | null = null): void {
        // Check if we're in element-modal context using contextElement if provided
        const searchRoot = commentForm.getSearchRoot(contextElement);

        if (!searchRoot) {
            console.warn("Search root not found for scrollToCommentById");
            return;
        }

        const commentsSection = commentForm.getCommentsSection(searchRoot);
        const targetComment = commentForm.getCommentItem(searchRoot, commentId);

        if (!commentsSection || !targetComment) {
            // Retry up to 3 times with delay (DOM might not be ready yet)
            if (retryCount < 3) {
                setTimeout(() => {
                    this.scrollToCommentById(commentId, retryCount + 1, contextElement);
                }, 100);
                return;
            }

            console.warn("Comments section or target comment not found", {
                commentsSection: !!commentsSection,
                targetComment: !!targetComment,
                commentId,
            });
            return;
        }

        // Scroll to target comment directly (allow scrolling up)
        targetComment.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
        });

        // Highlight effect after a brief delay to ensure scroll completes
        setTimeout(() => {
            targetComment.classList.add("comment-highlight");
            setTimeout(() => {
                targetComment.classList.remove("comment-highlight");
            }, 2000);
        }, 300);
    }

    /**
     * Update comment count display
     */
    private syncParentCommentCount(newCount: number): void {
        if (!modalHandler?.state || !commentForm.isElementModalOpen()) {
            return;
        }

        try {
            const { postId } = CommentApiService.getBoardAndPostIds();
            if (postId) {
                modalHandler.state.syncToParent(postId, "edit", "comments_count", newCount);
            }
        } catch (error) {
            console.error("Failed to update parent page comment count:", error);
        }
    }

    private updateCommentCountElements(root: ParentNode, newCount: number): void {
        const nextCount = String(newCount);
        const allActionElements = commentForm.getCommentCountActionElements(root);
        const allHeaderElements = commentForm.getCommentCountHeaderElements(root);

        allActionElements.forEach((element) => {
            element.textContent = nextCount;
        });

        allHeaderElements.forEach((element) => {
            element.textContent = nextCount;
        });
    }

    public updateCommentCountDisplay(newCount: number, contextElement: Element | null = null): void {
        const isInElementModal = commentForm.isElementModalOpen();

        const searchRoot = contextElement ? commentForm.getSearchRoot(contextElement) : document;
        this.updateCommentCountElements(searchRoot, newCount);

        if (isInElementModal) {
            this.syncParentCommentCount(newCount);
        }
    }

    /**
     * Restore comment like states from parent postsData
     */
    public restoreCommentLikeStates(postId: number): void {
        const postData = this.getCommentPostData(postId);
        if (!postData) {
            return;
        }

        // Prefer dedicated storage; fall back to legacy storage if present (and not an array)
        const commentLikeStates =
            postData.comment_like_states || (!Array.isArray(postData.comments) ? postData.comments : null);

        if (!commentLikeStates || Array.isArray(commentLikeStates)) {
            return;
        }

        // Determine search root based on modal context
        const searchRoot = commentForm.getSearchRoot();

        if (!searchRoot) {
            return;
        }

        // Update each comment's like button state
        Object.keys(commentLikeStates).forEach((commentIdKey) => {
            const commentId = Number(commentIdKey);
            if (!Number.isFinite(commentId) || commentId <= 0) {
                return;
            }

            const likeState = commentLikeStates[commentIdKey] as
                | { is_liked?: boolean; likes_count?: number }
                | undefined;
            const likeBtn = commentForm.getCommentLikeButton(searchRoot, commentId);

            if (likeBtn && likeState) {
                this.likeRender.updateLikeUI(likeBtn, likeState.is_liked, likeState.likes_count);
            }
        });
    }

    /**
     * Save comment like states to parent postsData
     */
    public saveCommentLikeStatesToParent(postId: number, comments: RenderableComment[]): void {
        const postData = this.getCommentPostData(postId);
        if (!postData) {
            return;
        }

        // Store like states separately from postData.comments (which is the comment list)
        if (!postData.comment_like_states || Array.isArray(postData.comment_like_states)) {
            postData.comment_like_states = {};
        }

        // Save each comment's like state
        comments.forEach((comment) => {
            const commentId = comment.comment_id || comment.id;
            if (commentId) {
                postData.comment_like_states[commentId] = {
                    is_liked: comment.is_liked || false,
                    likes_count: comment.likes_count || 0,
                };
            }
        });
    }
}

// Export singleton instance
const commentHandler = new CommentHandler();
export { commentHandler, CommentHandler };
